import { useMemo, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getSectorTaxonomy } from "../../steps/registry";
import SearchField from "./SearchField";
import SectorRow from "./SectorRow";
import { EXCLUSIVE_SECTOR_OPTIONS } from "./sectorSelection";
import { onboardingTokens } from "../../theme";
import { t } from "../../../../locales/t";

/**
 * Pick aviation sectors, and sub-types for the seven that have them.
 *
 * ## Controlled for selection, uncontrolled for search
 *
 * `value`/`onChange` carry the `SectorSelection`, which is flow data and gets
 * persisted. The search text stays here, because it is view state: put it in
 * the sub-flow next to the answers and someone eventually persists it too, and
 * a flow restored with a stale filter — hiding sectors the member already
 * picked — is a genuinely confusing bug.
 *
 * ## Advisory, not a limit
 *
 * Past nine selections the member is told the onboarding gets long, but nothing
 * is blocked. The design also hides the follow-up questions past three
 * selections; ECH-116 asks for them to always show for the sectors that have
 * them, and Linear outranks the export, so they always show.
 *
 * @param {object} props
 * @param {{primary: string[], secondary: Record<string, string[]>}} props.value
 * @param {(next: object) => void} props.onChange
 * @param {"seeking"|"offering"} [props.mode]
 * @param {string} props.label
 * @param {string} [props.subtitle]
 */
const MANY_SELECTED = 9;
const AviationSectorPicker = ({
  value,
  onChange,
  mode = "seeking",
  label,
  subtitle,
}) => {
  const [search, setSearch] = useState("");
  const { sectors, secondary } = getSectorTaxonomy(mode);

  // Filters on the LABEL, not the id: a member searching "rotary" must find
  // `pilot_helicopter_rotary_wing`, and typing "pilot_" should find nothing.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return sectors;
    return sectors.filter((id) =>
      t(`onboarding.vocab.${id}`).toLowerCase().includes(needle),
    );
  }, [sectors, search]);

  const togglePrimary = (sector) => {
    const isSelected = value.primary.includes(sector);
    const nextSecondary = { ...value.secondary };

    // Deselecting drops the sub-types with it — the same invalidate-downstream
    // rule the reducer applies to goalData when a goal is deselected. Without
    // it a member could submit sub-types for a sector they had removed.
    if (isSelected) delete nextSecondary[sector];

    onChange({
      primary: isSelected
        ? value.primary.filter((s) => s !== sector)
        : [...value.primary, sector],
      secondary: nextSecondary,
    });
  };

  const toggleOption = (sector, option) => {
    const current = value.secondary[sector] ?? [];
    const selected = current.includes(option);
    const next = selected
      ? current.filter((currentOption) => currentOption !== option)
      : EXCLUSIVE_SECTOR_OPTIONS.has(option)
        ? [option]
        : [
            ...current.filter(
              (currentOption) => !EXCLUSIVE_SECTOR_OPTIONS.has(currentOption),
            ),
            option,
          ];

    onChange({
      ...value,
      secondary: {
        ...value.secondary,
        [sector]: next,
      },
    });
  };

  const clearAll = () => onChange({ primary: [], secondary: {} });

  return (
    <Stack gap={1.5}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", mb: 0.5 }}>
          {label}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: onboardingTokens.mutedForeground,
              lineHeight: 1.55,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <SearchField
        value={search}
        onChange={setSearch}
        onClear={() => setSearch("")}
        placeholder={t("onboarding.sectors.searchPlaceholder")}
        label={t("onboarding.sectors.searchLabel")}
      />

      {/* The count is mounted whatever it reads, empty included.
          A live region announces changes to text it was already watching; one
          inserted with its first value is new content, not a change, and is
          announced by nothing. Wrapping this in `value.primary.length > 0`
          silenced the first selection and every one after it spoke. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          aria-live="polite"
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: onboardingTokens.teal,
          }}
        >
          {value.primary.length > 0
            ? t("onboarding.sectors.selected", { count: value.primary.length })
            : ""}
        </Typography>
        {value.primary.length > 0 && (
          <Button
            type="button"
            onClick={clearAll}
            sx={{
              fontSize: "0.8125rem",
              color: onboardingTokens.mutedForeground,
              minWidth: 0,
              p: 0,
            }}
          >
            {t("onboarding.sectors.clearAll")}
          </Button>
        )}
      </Stack>

      {/* Scrolls inside its own box rather than making the page long: with 31
          sectors an unbounded list buries the Continue button. */}
      <Box
        data-testid="sector-list"
        sx={{
          maxHeight: 280,
          overflowY: "auto",
          borderRadius: "0.75rem",
          border: `1.5px solid ${onboardingTokens.track}`,
          backgroundColor: onboardingTokens.card,
        }}
      >
        {filtered.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: onboardingTokens.mutedForeground,
              }}
            >
              {t("onboarding.sectors.noMatches", { search })}
            </Typography>
          </Box>
        ) : (
          filtered.map((sector, i) => (
            <SectorRow
              key={sector}
              sector={sector}
              selected={value.primary.includes(sector)}
              chosenOptions={value.secondary[sector]}
              options={secondary[sector]}
              mode={mode}
              onToggle={() => togglePrimary(sector)}
              onToggleOption={(option) => toggleOption(sector, option)}
              divider={i < filtered.length - 1}
            />
          ))
        )}
      </Box>

      {value.primary.length > MANY_SELECTED && (
        <Stack
          direction="row"
          gap={1.25}
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: "0.75rem",
            backgroundColor: onboardingTokens.lightTeal,
            border: `1px solid ${onboardingTokens.track}`,
          }}
        >
          <InfoOutlinedIcon
            aria-hidden="true"
            sx={{
              fontSize: 16,
              mt: "1px",
              flexShrink: 0,
              color: onboardingTokens.teal,
            }}
          />
          <Typography
            sx={{
              fontSize: "0.8125rem",
              lineHeight: 1.55,
              color: onboardingTokens.mutedForeground,
            }}
          >
            {t("onboarding.sectors.manySelected")}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default AviationSectorPicker;
